import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { ref, onValue, off } from "firebase/database";
import { rtdb } from "../config/firebase";
import { useNavigate } from "react-router-dom";
import { Calendar, ArrowLeft, ArrowRight, Search } from "lucide-react";

export default function NewsListPage() {
  const navigate = useNavigate();
  const [newsData, setNewsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  // ✅ Mulai halaman dari atas
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 🔹 Ambil data berita dari Firebase
  useEffect(() => {
    const newsRef = ref(rtdb, "berita");

    const unsubscribe = onValue(
      newsRef,
      (snapshot) => {
        const data = snapshot.val();
        const loaded = [];

        if (data) {
          // 🔁 Urutkan agar data terbaru muncul duluan
          for (const id in data) {
            const item = data[id];
            loaded.unshift({
              id,
              title: item.judul,
              content: item.keterangan,
              excerpt: item.keterangan?.slice(0, 100) + "...",
              date: item.tanggal,
              time: item.waktu,
              category: item.category || "Berita",
              image: item.fotoUrl,
            });
          }
        }

        setNewsData(loaded);
      },
      (error) => {
        console.error("❌ Gagal memuat berita:", error);
      },
    );

    return () => off(newsRef, "value", unsubscribe);
  }, []);

  // 🔍 Filter berita berdasarkan pencarian & tanggal
  const filteredNews = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    return newsData.filter((news) => {
      const matchSearch =
        news.title?.toLowerCase().includes(lower) ||
        news.content?.toLowerCase().includes(lower);

      const matchDate = !selectedDate || news.date === selectedDate;
      return matchSearch && matchDate;
    });
  }, [newsData, searchTerm, selectedDate]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 🔙 Tombol Kembali */}
      <motion.button
        onClick={() => navigate("/")}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="fixed top-6 left-6 z-50 flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 backdrop-blur-sm text-white hover:bg-black/50 transition-colors"
      >
        <ArrowLeft size={16} />
        <span className="text-sm font-semibold">Kembali</span>
      </motion.button>

      {/* 📰 Hero Section */}
      <div className="relative bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 text-white py-24 md:py-32 px-6 text-center overflow-hidden">
        {/* Efek cahaya lembut */}
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-green-400/30 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-teal-400/20 rounded-full blur-[120px]"></div>
        </div>

        {/* Pola grid lembut */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:48px_48px]"></div>

        <div className="relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            Berita & Informasi
          </h1>
          <p className="text-lg text-green-100 max-w-2xl mx-auto">
            Temukan kabar terbaru Soney Raya.
          </p>
        </div>
      </div>

      {/* 📋 Konten Utama */}
      <div className="relative bg-white -mt-16 rounded-t-3xl shadow-xl py-16 px-6 md:px-16">
        <div className="max-w-7xl mx-auto">
          {/* 🔍 Search & Filter */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-12">
            {/* Search Bar */}
            <div className="relative w-full max-w-md">
              <Search
                className="absolute left-4 top-3.5 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Cari berita berdasarkan judul atau isi..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition"
              />
            </div>

            {/* Filter Tanggal */}
            <div className="relative w-full max-w-xs">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-4 py-3 rounded-full border border-gray-300 bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:bg-white transition"
              />
              {selectedDate && (
                <button
                  onClick={() => setSelectedDate("")}
                  className="absolute right-4 top-3 text-gray-400 hover:text-red-500 text-sm"
                  title="Hapus filter tanggal"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* 📰 Grid Berita */}
          {filteredNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((news) => (
                <motion.div
                  key={news.id}
                  whileHover={{ scale: 1.02 }}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col border border-gray-200 cursor-pointer"
                  onClick={() => navigate(`/news/${news.id}`)}
                >
                  <div className="relative h-56">
                    <img
                      src={news.image}
                      alt={news.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  </div>

                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-sm font-semibold text-green-600 mb-2">
                      {news.category}
                    </p>
                    <h3 className="font-bold text-xl text-gray-900 line-clamp-2 mb-2 group-hover:text-green-700 transition-colors">
                      {news.title}
                    </h3>
                    <p className="text-gray-600 text-sm flex-1 line-clamp-4">
                      {news.content?.replace(/<[^>]+>/g, "")}
                    </p>
                    <div className="flex items-center justify-between mt-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar size={14} /> {news.date}
                      </span>
                      <ArrowRight size={16} className="text-green-600" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-16">
              Tidak ada berita yang cocok dengan pencarian atau tanggal.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
